
import { useState } from 'react';
import { toast } from 'sonner';
import { useUserRole } from "@/hooks/useUserRole";

export const useAllocationManager = (altAllocations: any, onAllocationsChange?: (any: any) => void) => {
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [hiddenSectors, setHiddenSectors] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [hasBeenEdited, setHasBeenEdited] = useState(false);
  const [editableAllocations, setEditableAllocations] = useState<Record<string, number>>({});
  const { userRole, isLoading: roleLoading } = useUserRole();

  const updateAllocation = (strategyId: string, percentage: number) => {
    if (roleLoading) {
      toast.error("Please wait while we verify your permissions");
      return;
    }

    if (!userRole) {
      toast.error("You don't have permission to update allocations");
      return;
    }

    setAllocations(prev => ({
      ...prev,
      [strategyId]: percentage
    }));
  };

  const handleEditStart = () => {
    if (roleLoading) {
      toast.error("Please wait while we verify your permissions");
      return;
    }

    if (!userRole) {
      toast.error("You don't have permission to edit allocations");
      return;
    }
    setIsEditing(true);
    // Initialize editable allocations with current values
    if (altAllocations) {
      setEditableAllocations({
        "Private Equity": altAllocations.private_equity_allocation || 0,
        "Private Credit": altAllocations.private_credit_allocation || 0,
        "Private Debt": altAllocations.private_debt_allocation || 0,
        "Real Estate": altAllocations.real_estate_allocation || 0,
        "Hedge Funds": altAllocations.hedge_funds_allocation || 0,
        "Commodities": altAllocations.commodities_allocation || 0,
        "Cryptocurrencies": altAllocations.cryptocurrencies_allocation || 0,
        "Collectibles": altAllocations.collectibles_allocation || 0
      });
    }
  };

  const updateHiddenSectors = (sector: string, allData: any[]) => {
    setHiddenSectors(prev => {
      const isHidden = prev.includes(sector);
      if (isHidden) {
        return prev.filter(s => s !== sector);
      } else {
        return [...prev, sector];
      }
    });
  };

  const handleAllocationChange = (name: string, value: number) => {
    setEditableAllocations(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getTotalAllocation = () => {
    return Object.values(allocations).reduce((sum, value) => sum + value, 0);
  };

  const validateAllocations = () => {
    const total = getTotalAllocation();
    return total === 100;
  };

  return {
    allocations,
    updateAllocation,
    getTotalAllocation,
    validateAllocations,
    isLoading: roleLoading,
    hiddenSectors,
    isEditing,
    hasBeenEdited,
    editableAllocations,
    setHasBeenEdited,
    setIsEditing,
    handleEditStart,
    updateHiddenSectors,
    handleAllocationChange
  };
};
